import React, { useEffect, useState, useContext, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../api/axios";
import { deletePost } from "../api/PostAPI";
import { fetchHome } from "../api/HomeAPI";
import AuthContext from "../context/AuthContext";
import PostCard from "../components/PostCard";
import {
  HomeEventEndMarker,
  HomeEventStartCard,
  NextEventBanner,
} from "../components/HomeEventItems";
import { useModal } from "../components/ModalManager";
import { occurrenceEngagementStore } from "../hooks/useOccurrenceEngagement";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { useEventChanges } from "../utils/eventChanges";
import { occurrencePreviewPath } from "../utils/eventDomain";
import {
  applyChangeToHome,
  fetchHomeToDepth,
  itemKey,
  mergeHomePage,
  shownNextEvent,
} from "../utils/homeTimeline";

// Upper bound on pages re-read when refreshing the loaded depth.
const MAX_REFRESH_PAGES = 10;

function Home() {
  const { token, user } = useContext(AuthContext);
  const { openModal } = useModal();

  // /home/ state: `items` is rendered exactly in backend order; `nextEvent`
  // is shown separately from the timeline, as the backend returns it.
  const [items, setItems] = useState([]);
  const [nextEvent, setNextEvent] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Bumped whenever the timeline is reset (initial load / refresh) so a
  // response for an older timeline is dropped instead of merged in.
  const generationRef = useRef(0);
  // Synchronous in-flight guard — state updates are too late to stop the
  // infinite-scroll observer from firing a duplicate request.
  const loadingRef = useRef(false);

  const location = useLocation();
  const navigate = useNavigate();

  const handleOpenPost = (post) => {
    navigate(`/posts/${post.id}`, {
      state: { backgroundLocation: location },
    });
  };

  // Event items carry an occurrence: open the shared Event focus view for
  // that occurrence (it loads the full Event itself). Changes made there come
  // back through useEventChanges.
  const handleOpenOccurrence = (occurrence) => {
    openModal("EventFocusModal", { eventId: occurrence.event_id, occurrence });
  };

  // Every event-start card — host, attendee or friend — opens the occurrence
  // preview, which decides what this viewer may see and do.
  const handleOpenStart = (occurrence) => {
    navigate(occurrencePreviewPath(occurrence.id), {
      state: { backgroundLocation: location },
    });
  };

  const fetchPage = useCallback(
    async (cursor = null) => {
      if (!token || loadingRef.current) return;

      const generation = generationRef.current;
      loadingRef.current = true;
      setLoading(true);
      occurrenceEngagementStore.ensureSession(token);
      const since = occurrenceEngagementStore.mark();
      try {
        const data = await fetchHome(token, { cursor });
        if (generation !== generationRef.current) return;
        occurrenceEngagementStore.seedHomeItems(data.items, since);

        if (!cursor) setNextEvent(shownNextEvent(data.next_event));
        // The cursor shouldn't produce overlap; mergeHomePage guards anyway so
        // a repeated page can never render duplicate rows.
        setItems((prev) => mergeHomePage(cursor ? prev : [], data.items));
        setNextCursor(data.next_cursor || null);
        setHasMore(Boolean(data.has_more && data.next_cursor));
      } catch (err) {
        console.error("Error fetching home timeline", err);
      } finally {
        if (generation === generationRef.current) {
          loadingRef.current = false;
          setLoading(false);
          setHasLoaded(true);
        }
      }
    },
    [token]
  );

  // Starts the timeline over: older in-flight
  // responses are discarded and the first page is fetched fresh.
  const resetTimeline = useCallback(() => {
    generationRef.current += 1;
    loadingRef.current = false;
    setItems([]);
    setNextEvent(null);
    setNextCursor(null);
    setHasMore(false);
    setHasLoaded(false);
    fetchPage(null);
  }, [fetchPage]);

  // First page on mount and whenever the token changes.
  useEffect(() => {
    resetTimeline();
  }, [resetTimeline]);

  const itemCountRef = useRef(0);
  itemCountRef.current = items.length;

  // After an Event change (edit, move, cancel, delete, RSVP), re-reads the
  // timeline from the top down to the depth already loaded, so rows reorder,
  // move or disappear exactly as the backend now orders them — the client
  // never re-sorts. Supersedes any in-flight page (generation bump); the
  // current rows stay up until the fresh ones replace them.
  const refreshLoadedDepth = useCallback(async () => {
    if (!token) return;
    const generation = ++generationRef.current;
    loadingRef.current = true;
    setLoading(true);
    occurrenceEngagementStore.ensureSession(token);
    const since = occurrenceEngagementStore.mark();
    try {
      const result = await fetchHomeToDepth((cursor) => fetchHome(token, { cursor }), {
        depth: Math.max(itemCountRef.current, 1),
        maxPages: MAX_REFRESH_PAGES,
        isCurrent: () => generation === generationRef.current,
      });
      if (!result) return;
      occurrenceEngagementStore.seedHomeItems(result.items, since);
      setNextEvent(result.nextEvent);
      setItems(result.items);
      setNextCursor(result.nextCursor);
      setHasMore(result.hasMore);
    } catch (err) {
      console.error("Error refreshing home timeline", err);
    } finally {
      if (generation === generationRef.current) {
        loadingRef.current = false;
        setLoading(false);
        setHasLoaded(true);
      }
    }
  }, [token]);

  // Every Event mutation is announced, wherever it was made. A confirmed
  // occurrence result is applied at once — a removed (or no longer attended)
  // occurrence's rows and Next Event go — so a failed re-read can't leave
  // them; the re-read (which supersedes any in-flight one) then fetches the
  // new Next Event and any reordering.
  const homeStateRef = useRef({ items: [], nextEvent: null });
  homeStateRef.current = { items, nextEvent };
  useEventChanges((change) => {
    const applied = applyChangeToHome(homeStateRef.current, change);
    if (applied.items !== homeStateRef.current.items) setItems(applied.items);
    if (applied.nextEventChanged) setNextEvent(applied.nextEvent);
    refreshLoadedDepth();
  });

  const handleLoadMore = useCallback(() => {
    if (hasMore && nextCursor) fetchPage(nextCursor);
  }, [hasMore, nextCursor, fetchPage]);

  const loaderRef = useInfiniteScroll(handleLoadMore);

  const removePostItem = (postId) => {
    setItems((prev) => prev.filter((item) => !(item.type === "post" && item.data.id === postId)));
  };

  const handleDeletePost = async (postId) => {
    try {
      await deletePost(postId);
      removePostItem(postId);
    } catch (err) {
      console.error("Failed to delete post", err);
    }
  };

  useEffect(() => {
    if (location.state?.refreshFeed) {
      resetTimeline();
      return;
    }

    const deletedPostId = location.state?.deletedPostId;
    if (deletedPostId != null) {
      removePostItem(deletedPostId);
      return;
    }

    const updatedPostId = location.state?.updatedPostId;

    if (!updatedPostId) return;

    const refreshPost = async () => {
      try {
        const res = await axiosInstance.get(`/posts/${updatedPostId}/`, {
          headers: { Authorization: `Token ${token}` },
        });

        setItems((prev) =>
          prev.map((item) =>
            item.type === "post" && item.data.id === updatedPostId ? { ...item, data: res.data } : item
          )
        );
      } catch (err) {
        console.error("Failed to refresh updated post", err);
      }
    };

    refreshPost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const renderItem = (item) => {
    switch (item.type) {
      case "post":
        return (
          <PostCard
            key={itemKey(item)}
            postData={item.data}
            focusPost={handleOpenPost}
            onDeletePost={handleDeletePost}
            user={user}
            token={token}
          />
        );
      case "event_start":
        return (
          <HomeEventStartCard key={itemKey(item)} occurrence={item.data} onOpen={handleOpenStart} />
        );
      case "event_end":
        return (
          <HomeEventEndMarker key={itemKey(item)} occurrence={item.data} onOpen={handleOpenOccurrence} />
        );
      default:
        // Unknown future item type: skip it rather than break the feed.
        return null;
    }
  };

  return (
    <>
      <div className="w-full mx-auto">
        <div className="flex justify-between items-center px-4 pt-4 pb-2 max-w-[480px] mx-auto">
          <h1 className="text-lg font-semibold">Feed</h1>
        </div>
        <div className="p-4">
          {nextEvent && <NextEventBanner occurrence={nextEvent} onOpen={handleOpenOccurrence} />}
          {hasLoaded && !loading && items.length === 0 && !nextEvent && (
            <p className="text-center">Check back later for fresh posts!</p>
          )}
          {items.map(renderItem)}
          <div ref={loaderRef} className="h-10"></div>
          {loading && <p className="text-center">Loading...</p>}
        </div>
      </div>
    </>
  );
}

export default Home;
